import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getAuthToken } from '../../../utils/authStorage';
import './ThermalExtrema.css';

function useStatistics(inspectionId, imageId, bounds, enabled) {
  const [state, setState] = useState({});
  useEffect(() => {
    if (!enabled || !imageId) { setState({}); return; }
    const controller = new AbortController();
    setState({ loading: true });
    axios.get(`/api/inspections/${inspectionId}/images/${imageId}/thermal/statistics`, {
      params: bounds || {}, signal: controller.signal,
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    }).then(response => {
      if (!controller.signal.aborted) setState({ data: response.data });
    }).catch(() => {
      if (!controller.signal.aborted) setState({ error: 'Temperature extrema unavailable. Check thermal data and server support.' });
    });
    return () => controller.abort();
  }, [inspectionId, imageId, bounds, enabled]);
  return state;
}

export default function ThermalExtrema({ inspectionId, imageId, rect, showGlobal, selecting, onSelected }) {
  const [area, setArea] = useState(null);
  const [draft, setDraft] = useState(null);
  const anchor = useRef(null);
  const global = useStatistics(inspectionId, imageId, null, showGlobal);
  const region = useStatistics(inspectionId, imageId, area, !!area);
  const point = event => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)), y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)) };
  };
  const box = (a, b) => ({ x1: Math.min(a.x, b.x), y1: Math.min(a.y, b.y), x2: Math.max(a.x, b.x), y2: Math.max(a.y, b.y) });
  const markers = (stats, scope) => ['min', 'max'].map(kind => {
    const value = stats.data?.[kind];
    if (!value || ![value.xNorm, value.yNorm, value.temperatureC].every(Number.isFinite)) return null;
    return <div key={`${scope}-${kind}`} className={`thermal-extremum thermal-extremum-${kind}`} style={{ left: `${value.xNorm * 100}%`, top: `${value.yNorm * 100}%` }}><span>{scope} {kind === 'min' ? 'Min' : 'Max'}: {value.temperatureC.toFixed(1)}°C</span></div>;
  });
  const selection = draft || area;
  return <div className="thermal-extrema" style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}>
    {selection && <div className="thermal-area" style={{ left: `${selection.x1 * 100}%`, top: `${selection.y1 * 100}%`, width: `${(selection.x2 - selection.x1) * 100}%`, height: `${(selection.y2 - selection.y1) * 100}%` }} />}
    {showGlobal && markers(global, 'Image')}
    {!draft && markers(region, 'Area')}
    {(selecting || global.loading || region.loading || global.error || region.error || area) && <div className="thermal-extrema-status" role="status">
      {selecting ? 'Drag to select an area. ' : ''}
      {global.loading || region.loading ? 'Calculating temperatures… ' : ''}
      {global.error || region.error}
      {area && <button type="button" onClick={event => { event.stopPropagation(); setArea(null); }}>Clear area</button>}
    </div>}
    {selecting && <div className="thermal-area-input" onMouseDown={event => event.stopPropagation()} onMouseMove={event => event.stopPropagation()} onPointerDown={event => {
      if (event.button !== 0) return;
      event.preventDefault(); event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      anchor.current = point(event); setDraft(null);
    }} onPointerMove={event => {
      if (anchor.current) setDraft(box(anchor.current, point(event)));
    }} onPointerUp={event => {
      if (!anchor.current) return;
      const bounds = box(anchor.current, point(event));
      anchor.current = null; setDraft(null);
      if (bounds.x2 > bounds.x1 && bounds.y2 > bounds.y1) { setArea(bounds); onSelected(); }
    }} onPointerCancel={() => { anchor.current = null; setDraft(null); }} />}
  </div>;
}
